<template>
  <div class="currency-container">
    <el-card class="currency-card">
      <template #header>
        <div class="card-header">
          <h2>Добро пожаловать в МНОС!</h2>
          <div class="exchange-rates">
            <el-divider>
              <span class="divider-text">Курсы валют</span>
            </el-divider>
            <div v-if="loadingRates" class="rates-loading">
              <el-skeleton :rows="2" animated />
            </div>
            <div v-else-if="exchangeRates.length > 0" class="rates-grid">
              <div v-for="rate in exchangeRates" :key="rate.currency" class="rate-item">
                <img :src="getCurrencyFlagUrl(rate.currency)" :alt="rate.currency" class="currency-flag" />
                <span class="currency-code">{{ rate.currency }}</span>
                <span class="rate-value">{{ rate.rate.toFixed(4) }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div class="card-content">
        <el-button
          type="primary"
          size="default"
          @click="goToLogin"
          class="login-button"
        >
          Войти в аккаунт
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import type { ExchangeRate } from '@/store/types'
import { getCurrencyFlagUrl } from '@/utils/currency'

export default defineComponent({
  name: 'CurrencyView',
  setup() {
    const store = useStore()
    const router = useRouter()
    const exchangeRates = ref<ExchangeRate[]>([])
    const loadingRates = ref(false)

    const fetchExchangeRates = async () => {
      loadingRates.value = true
      try {
        const rates = await store.dispatch('fetchExchangeRates')
        exchangeRates.value = rates
      } catch (err) {
        ElMessage.error('Не удалось загрузить курсы валют')
      } finally {
        loadingRates.value = false
      }
    }

    const goToLogin = () => {
      router.push('/login')
    }

    onMounted(() => {
      fetchExchangeRates()
    })

    return {
      exchangeRates,
      loadingRates,
      getCurrencyFlagUrl,
      goToLogin
    }
  }
})
</script>

<style scoped>
.currency-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.currency-card {
  width: 100%;
  max-width: 420px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.card-header {
  text-align: center;
  padding: 0 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
  font-weight: 600;
}

.card-content {
  text-align: center;
  padding: 20px;
}

.login-button {
  padding: 12px 32px;
  font-size: 16px;
}

.exchange-rates {
  margin-top: 16px;
}

.divider-text {
  text-wrap: nowrap;
  color: #909399;
  font-size: 14px;
  padding: 0 12px;
}

.rates-loading {
  padding: 16px;
}

.rates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.rate-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 6px;
}

.currency-flag {
  width: 24px;
  height: 16px;
  object-fit: cover;
  border-radius: 2px;
}

.currency-code {
  font-weight: 500;
  color: #303133;
}

.rate-value {
  font-weight: 600;
}

:deep(.el-card__header) {
  padding: 24px 20px 12px 24px;
  border-bottom: 1px solid #ebeef5;
}

:deep(.el-card__body) {
  padding: 0;
}

@media (max-width: 480px) {
  .currency-card {
    max-width: 80%;
  }
  
  .card-header h2 {
    font-size: 20px;
  }
}
</style> 